import { Button } from '../button'
import React, { useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useDropzone } from 'react-dropzone'
import { useQueryClient } from '@tanstack/react-query'
import { receiptQueries } from '../../services/receipts/queries'
import { ArrowPathIcon, CheckCircleIcon, XMarkIcon } from '@heroicons/react/24/outline'
import {
  useReceiptUploads,
  type ReceiptUploadItem,
  type UploadError,
} from 'src/hooks/use-receipt-uploads'
import type { Receipt } from 'src/services/receipts/models'
import {
  RECEIPT_UPLOAD_MAX_FILES,
  RECEIPT_UPLOAD_MAX_FILE_SIZE_MB,
} from 'src/services/receipts/upload-limits'
import { cn } from 'src/utils/style'

interface ReceiptUploadProps {
  onPostUpload?: (receipts: Receipt[]) => void
}

export const ReceiptUpload: React.FC<ReceiptUploadProps> = ({ onPostUpload }) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const {
    items,
    addFiles,
    rename,
    remove,
    reset,
    uploadAll,
    retry,
    uploadedCount,
    rejectedCount,
    totalCount,
    isUploading,
  } = useReceiptUploads()

  const onDrop = useCallback((acceptedFiles: File[]) => addFiles(acceptedFiles), [addFiles])
  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop })

  const hasStarted = items.some((it) => it.status !== 'pending')
  const pendingCount = items.filter((it) => it.status === 'pending').length

  // When every staged file has uploaded, refresh the list, hand the created
  // receipts back, and clear the control — matching a finished Drive drop.
  // Hold off while rejected rows remain so their message stays visible.
  useEffect(() => {
    if (totalCount > 0 && uploadedCount === totalCount && rejectedCount === 0) {
      const receipts = items.flatMap((it) => (it.receipt ? [it.receipt] : []))
      queryClient.invalidateQueries({ queryKey: receiptQueries.all() })
      onPostUpload?.(receipts)
      reset()
    }
  }, [uploadedCount, totalCount, rejectedCount, items, queryClient, onPostUpload, reset])

  const handleUpload = async () => {
    await uploadAll()
    queryClient.invalidateQueries({ queryKey: receiptQueries.all() })
  }

  const handleRetry = async (id: string) => {
    await retry(id)
    queryClient.invalidateQueries({ queryKey: receiptQueries.all() })
  }

  return (
    <div className="mb-4">
      <div
        {...getRootProps()}
        className={`cursor-pointer rounded-md border-2 border-dashed p-8 text-center ${
          isDragActive ? 'border-accent-500' : 'border-border-strong'
        }`}
        data-testid="dropzone"
      >
        <input {...getInputProps()} />
        {isDragActive ? <p>{t('receipts.dropHere')}</p> : <p>{t('receipts.dragDrop')}</p>}
        <p className="mt-2 text-xs text-text-placeholder" data-testid="upload-limits">
          {t('receipts.uploadLimits', {
            maxFiles: RECEIPT_UPLOAD_MAX_FILES,
            maxSize: RECEIPT_UPLOAD_MAX_FILE_SIZE_MB,
          })}
        </p>
      </div>

      {items.length > 0 && (
        <div className="mt-4 space-y-2">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-medium text-gray-700">{t('receipts.filesToUpload')}</h4>
            {hasStarted && (
              <span className="text-xs text-text-placeholder" data-testid="upload-aggregate">
                {t('receipts.uploadedOf', { uploaded: uploadedCount, total: totalCount })}
              </span>
            )}
          </div>
          <div className="space-y-2">
            {items.map((item) => (
              <ReceiptUploadRow
                key={item.id}
                item={item}
                onRename={rename}
                onRemove={remove}
                onRetry={handleRetry}
              />
            ))}
          </div>
        </div>
      )}

      {pendingCount > 0 && (
        <Button
          type="button"
          onClick={handleUpload}
          disabled={isUploading}
          variant="primary"
          className="mt-4 w-full"
          data-testid="upload-button"
        >
          {isUploading
            ? t('common.uploading')
            : t(pendingCount > 1 ? 'receipts.upload_plural' : 'receipts.upload', {
                count: pendingCount,
              })}
        </Button>
      )}
    </div>
  )
}

interface ReceiptUploadRowProps {
  item: ReceiptUploadItem
  onRename: (id: string, name: string) => void
  onRemove: (id: string) => void
  onRetry: (id: string) => void
}

const ReceiptUploadRow: React.FC<ReceiptUploadRowProps> = ({
  item,
  onRename,
  onRemove,
  onRetry,
}) => {
  const { t } = useTranslation()
  const isEditable = item.status === 'pending' || item.status === 'failed'
  const isRemovable = isEditable || item.status === 'rejected'

  const rejectionMessage = (): string => {
    switch (item.rejectionReason) {
      case 'file-too-large':
        return t('receipts.rejectedTooLarge', { maxSize: RECEIPT_UPLOAD_MAX_FILE_SIZE_MB })
      case 'too-many-files':
        return t('receipts.rejectedTooManyFiles', { maxFiles: RECEIPT_UPLOAD_MAX_FILES })
      case 'unsupported-type':
      default:
        return t('receipts.rejectedUnsupportedType')
    }
  }

  // A `raw` server/Error message is shown verbatim; status/generic are localized.
  const errorMessage = (error: UploadError): string => {
    switch (error.kind) {
      case 'raw':
        return error.message
      case 'status':
        return t('receipts.uploadFailedStatus', { status: error.status })
      case 'generic':
      default:
        return t('receipts.uploadFailed')
    }
  }

  return (
    <div
      className="flex items-center gap-2 rounded-lg bg-surface-hover p-3 shadow-sm"
      data-testid="upload-row"
      data-status={item.status}
    >
      <div className="flex-1">
        {isEditable ? (
          <input
            type="text"
            value={item.name}
            onChange={(e) => onRename(item.id, e.target.value)}
            className="w-full rounded border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent-500"
            placeholder={t('receipts.receiptName')}
          />
        ) : (
          <span className="block truncate text-sm text-gray-700">{item.name}</span>
        )}

        {(item.status === 'uploading' || item.status === 'uploaded') && (
          <div className="mt-2 flex items-center gap-2">
            <div className="h-2 flex-1 overflow-hidden rounded-full bg-primary-100">
              <div
                className={cn(
                  'h-full rounded-full transition-all duration-300',
                  item.status === 'uploaded' ? 'bg-green-500' : 'bg-primary-500',
                )}
                style={{ width: `${item.progress}%` }}
                data-testid="upload-progress-bar"
                role="progressbar"
                aria-valuenow={item.progress}
                aria-valuemin={0}
                aria-valuemax={100}
              />
            </div>
            <span className="w-9 text-right text-xs tabular-nums text-text-placeholder">
              {item.progress}%
            </span>
          </div>
        )}

        {item.status === 'failed' && item.error && (
          <span className="mt-1 block text-xs text-error" data-testid="upload-error">
            {errorMessage(item.error)}
          </span>
        )}
        {item.status === 'rejected' && (
          <span className="mt-1 block text-xs text-error" data-testid="upload-rejected">
            {rejectionMessage()}
          </span>
        )}
        {item.status === 'pending' && (
          <span className="mt-1 block text-xs text-text-placeholder">
            {t('receipts.original')} {item.originalName}
          </span>
        )}
      </div>

      {item.status === 'uploaded' && (
        <span className="flex items-center gap-1 text-xs text-green-600" data-testid="upload-done">
          <CheckCircleIcon className="h-4 w-4" aria-hidden="true" />
          {t('receipts.uploaded')}
        </span>
      )}

      {item.status === 'failed' && (
        <Button
          variant="ghost"
          tone="primary"
          size="sm"
          icon={<ArrowPathIcon className="h-4 w-4" />}
          onClick={() => onRetry(item.id)}
          type="button"
          data-testid="upload-retry"
        >
          {t('receipts.retry')}
        </Button>
      )}

      {isRemovable && (
        <Button
          variant="ghost"
          tone="error"
          iconOnly
          shape="square"
          onClick={() => onRemove(item.id)}
          title={t('common.remove')}
          type="button"
        >
          <XMarkIcon className="h-4 w-4" />
        </Button>
      )}
    </div>
  )
}
